// VerifyEmailComponent.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import {
  emailAtom,
  registeredUserIdAtom,
  submitAccessibilityAtom,
} from "@/store/atoms";
import Loader from "@/components/common/Loader";
import {
  useAccessibility,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import WideButton from "@/components/common/WideButton";

interface VerifyEmailProps {
  title: string;
  subtitle: string;
}

const VerifyEmailComponent = ({ title, subtitle }: VerifyEmailProps) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const [email] = useAtom(emailAtom);
  const { setScreenIndex } = useAuthModals();
  const { modalStyle, modalClassName } = useModalHeight();
  const [registeredUserId] = useAtom(registeredUserIdAtom);

  const {
    submitAccessibility,
    submitAccessibilityError,
    submitAccessibilityResponse,
    submitAccessibilityStatus,
  } = useAccessibility();

  //get and store the data for entire onBoarding steps
  const [submitData] = useAtom(submitAccessibilityAtom);

  const isLoading = submitAccessibilityStatus === "pending";

  useEffect(() => {
    if (submitData && registeredUserId) {
      const dataToSubmit = {
        ...submitData,
        userId: registeredUserId,
      };
      submitAccessibility(dataToSubmit);
    }
  }, [submitData, registeredUserId, submitAccessibility]);

  useEffect(() => {
    if (submitAccessibilityError) {
      console.log(
        "submitAccessibilityError",
        submitAccessibilityError?.message,
      );
    }
  }, [submitAccessibilityError]);

  useEffect(() => {
    if (submitAccessibilityResponse) {
      console.log(
        "submitAccessibilityResponse",
        submitAccessibilityResponse?.message,
      );

      const userDataCompat = {
        id: submitAccessibilityResponse.user_id,
        ability: "",
        accessibility_need: "",
        email: submitAccessibilityResponse.email,
        gender: "",
        name: submitAccessibilityResponse.name,
        year_of_birth: "",
        level: 1,
      };

      AnalyticsService.setUserProfileProps(
        userDataCompat,
        {
          EmailConfirmed: false,
          HowDoYouHearAboutSociability: "",
        },
        {
          EmailConfirmed: false,
        },
      );
    }
  }, [email, submitAccessibilityResponse]);

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountGotoLoginClickedOnVerification",
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountGotoLoginClickedOnVerification",
      {},
    );

    //move to Login
    setScreenIndex(AuthModalScreen.EnterEmail);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  console.log("submitData from OnboardingStep5", submitData);

  return (
    <div
      style={modalStyle}
      className="flex flex-col rounded-xl bg-bg-brand-secondary-default overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {i18n.get("verifyEmailAddress")}
        </h1>
      </header>
      <div className="flex-1 px-8 md:px-16 overflow-y-auto">
        <div className="mt-6">
          <h2 className="mb-4 text-fg-neutral-strong title-m-bold">{title}</h2>
          <p className="title-s-semibold text-fg-neutral-strong">{subtitle}</p>
        </div>
        <div>
          <hr className="w-full border-border-token-brand-minimal my-6" />
        </div>
        <div aria-label="Model Content">
          {/* content comes here */}
          <div>
            <p className="title-s-semibold text-fg-neutral-strong mb-3">
              {i18n.get("wehaveJustSentAnEmailTo")}
            </p>
            <p className="mb-6 title-s-semibold !text-fg-brand-subtle">
              {email}
            </p>
            <p className="label-m-medium text-fg-neutral-strong">
              {i18n.get("clickTheLinkInsideToLogInToSociability")}
            </p>
          </div>
          {/* content comes here */}
        </div>
        {isLoading && <Loader />}
        <div className="mb-16 mt-8">
          <WideButton onClick={onContinue}>
            {i18n.get("returnToLoginPage")}
          </WideButton>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailComponent;
